import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

interface Session {
  id: string
  session_code: string
  room_name: string
  creator_name: string
  creator_role: string
  customer_note?: string
  status: 'active' | 'ended'
  participant_count: number
  created_at: string
  duration?: number
}

interface Agent {
  id: string
  name: string
  email: string
  status: 'online' | 'offline' | 'in_session'
  active_sessions: number
}

interface Organization {
  id: string
  name: string
  total_agents: number
  active_sessions: number
}

const Supervisor = (): JSX.Element => {
  const [activeSessions, setActiveSessions] = useState<Session[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [selectedSession, setSelectedSession] = useState<Session | null>(null)
  const [isJoining, setIsJoining] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    loadSupervisorData()
    // Set up real-time updates
    const interval = setInterval(loadSupervisorData, 30000) // Refresh every 30s
    return () => clearInterval(interval)
  }, [])

  const loadSupervisorData = async (): Promise<void> => {
    try {
      // TODO: Replace with real API calls
      setOrganization({
        id: 'demo-org-123',
        name: 'Demo Organization',
        total_agents: 5,
        active_sessions: 2
      })

      setAgents([
        {
          id: 'agent-1',
          name: 'John Smith',
          email: 'john@company.com',
          status: 'in_session',
          active_sessions: 1
        },
        {
          id: 'agent-2', 
          name: 'Sarah Johnson',
          email: 'sarah@company.com',
          status: 'online',
          active_sessions: 0
        },
        {
          id: 'agent-3',
          name: 'Mike Chen',
          email: 'mike@company.com',
          status: 'in_session',
          active_sessions: 1
        },
        {
          id: 'agent-4',
          name: 'Lisa Wilson',
          email: 'lisa@company.com',
          status: 'offline',
          active_sessions: 0
        }
      ])

      setActiveSessions([
        {
          id: 'session-1',
          session_code: 'ABC123',
          room_name: 'room-abc123',
          creator_name: 'John Smith',
          creator_role: 'Agent',
          customer_note: 'Customer having trouble with login screen - seems to be a browser issue',
          status: 'active',
          participant_count: 2,
          created_at: new Date(Date.now() - 900000).toISOString(),
          duration: 15
        },
        {
          id: 'session-2',
          session_code: 'XYZ789',
          room_name: 'room-xyz789',
          creator_name: 'Mike Chen',
          creator_role: 'Agent',
          customer_note: 'Payment processing error on checkout page',
          status: 'active',
          participant_count: 1,
          created_at: new Date(Date.now() - 300000).toISOString(),
          duration: 5
        }
      ])
    } catch (error) {
      console.error('Failed to load supervisor data:', error)
    }
  }

  const joinSession = async (session: Session): Promise<void> => {
    setIsJoining(true)
    setSelectedSession(session)
    
    try {
      // TODO: Replace with real API call to join session
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Update participant count
      setActiveSessions(prev => prev.map(s => 
        s.id === session.id 
          ? { ...s, participant_count: s.participant_count + 1 }
          : s
      ))
      
      // TODO: Initialize LiveKit connection as observer
      
    } catch (error) {
      console.error('Failed to join session:', error)
      alert('Failed to join session. Please try again.')
      setSelectedSession(null)
    } finally {
      setIsJoining(false)
    }
  }

  const leaveSession = (): void => {
    if (selectedSession) {
      // Update participant count
      setActiveSessions(prev => prev.map(s => 
        s.id === selectedSession.id 
          ? { ...s, participant_count: Math.max(1, s.participant_count - 1) }
          : s
      ))
      setSelectedSession(null)
    }
  }

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'online': return 'bg-green-100 text-green-800'
      case 'in_session': return 'bg-blue-100 text-blue-800'
      case 'offline': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading supervisor dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Supervisor Dashboard</h1>
              <p className="text-sm text-gray-600">{organization.name} • Real-time Session Monitoring</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-6 text-sm">
                <div>
                  <span className="text-gray-600">Active Sessions: </span>
                  <span className="font-semibold text-blue-600">{organization.active_sessions}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Agents: </span>
                  <span className="font-semibold">{organization.total_agents}</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Session Observer */}
            {selectedSession && (
              <div className="bg-white rounded-lg shadow p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Observing Session: {selectedSession.session_code}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Agent: {selectedSession.creator_name} • Duration: {selectedSession.duration}min
                    </p>
                  </div>
                  <button
                    onClick={leaveSession}
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 text-sm"
                  >
                    Leave Session
                  </button>
                </div>
                
                {/* Video placeholder - TODO: Replace with LiveKit component */}
                <div className="bg-gray-900 rounded-lg aspect-video flex items-center justify-center mb-4">
                  <div className="text-white text-center">
                    <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <p className="text-lg">Observing Session (Silent Mode)</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {selectedSession.participant_count} participants • You are observing silently
                    </p>
                  </div>
                </div>

                {/* Session Info */}
                {selectedSession.customer_note && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Issue Description:</h4>
                    <p className="text-blue-800">{selectedSession.customer_note}</p>
                  </div>
                )}
              </div>
            )}

            {/* Active Sessions */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Active Sessions</h2>
                <p className="text-sm text-gray-600 mt-1">Click on any session to observe silently</p>
              </div>
              <div className="divide-y divide-gray-200">
                {activeSessions.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-3a2 2 0 01-2-2V9a2 2 0 00-2-2H9a2 2 0 00-2 2v2a2 2 0 01-2 2H2"/>
                    </svg>
                    <p className="text-lg font-medium mb-2">No active sessions</p>
                    <p>All agents are currently available. Sessions will appear here when agents are helping customers.</p>
                  </div>
                ) : (
                  activeSessions.map((session) => (
                    <div key={session.id} className="p-6 hover:bg-gray-50 cursor-pointer" onClick={() => joinSession(session)}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <span className="text-lg font-mono font-bold text-blue-600">
                              {session.session_code}
                            </span>
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                            <span className="text-sm text-gray-600">
                              {session.duration}min
                            </span>
                          </div>
                          <div className="mt-1">
                            <p className="text-sm font-medium text-gray-900">
                              Agent: {session.creator_name}
                            </p>
                            {session.customer_note && (
                              <p className="text-sm text-gray-600 mt-1">{session.customer_note}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Started: {new Date(session.created_at).toLocaleString()} • 
                              {session.participant_count} participant{session.participant_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {session.participant_count > 1 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                              👥 {session.participant_count} people
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              joinSession(session)
                            }}
                            disabled={isJoining}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm disabled:opacity-50"
                          >
                            {isJoining && selectedSession?.id === session.id ? 'Joining...' : 'Observe'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Team Status */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Status</h3>
              <div className="space-y-3">
                {agents.map((agent) => (
                  <div key={agent.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        agent.status === 'online' ? 'bg-green-500' :
                        agent.status === 'in_session' ? 'bg-blue-500' : 'bg-gray-400'
                      }`}></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                        <p className="text-xs text-gray-500">{agent.email}</p>
                      </div>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(agent.status)}`}>
                      {agent.status.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Sessions Today</span>
                  <span className="font-semibold">12</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Avg Duration</span>
                  <span className="font-semibold">8.5min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Peak Sessions</span>
                  <span className="font-semibold">4</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Success Rate</span>
                  <span className="font-semibold text-green-600">94%</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/agent')}
                  className="w-full text-left px-4 py-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg"
                >
                  🎯 Switch to Agent View
                </button>
                <button className="w-full text-left px-4 py-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg">
                  📊 View Full Analytics
                </button>
                <button className="w-full text-left px-4 py-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg">
                  ⚙️ Manage Team Settings
                </button>
              </div>
            </div>

            {/* Supervisor Tips */}
            <div className="bg-purple-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-4">Supervisor Tips</h3>
              <div className="space-y-3 text-sm text-purple-800">
                <p>👁️ You can silently observe any session without disrupting the flow</p>
                <p>📞 Jump in to help when agents need support</p>
                <p>📈 Monitor team performance and session success rates</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Supervisor
